import React, { useState, useEffect, useMemo } from 'react';
import { supabase, UserCoins, CoinTransaction, coinPackages, updateUserCoins, getUserCoins, getUserTransactions, useCoupon } from '../lib/supabase';
import { User } from '@supabase/supabase-js';
import { Sparkles, TrendingUp, TrendingDown, History, ChevronDown, ChevronUp, Zap, FileText, Brain, Cpu, Layers } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { PaykuService } from '../services/paykuService';
import { useCurrencyPackages } from '../hooks/useCurrencyPackages';
import { Currency, formatCurrency, saveCurrencyPreference } from '../config/currency';
import { PayPalCheckout } from './PayPalCheckout';

interface CoinsManagerProps {
  user: User | null;
  onCoinsUpdated?: () => void;
  renderCouponSection?: () => React.ReactNode;
  formatNumber?: (num: number) => string;
}

const CoinsManager = ({ user, onCoinsUpdated, renderCouponSection, formatNumber }: CoinsManagerProps) => {
  const [userCoins, setUserCoins] = useState<UserCoins | null>(null);
  const [transactions, setTransactions] = useState<CoinTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState<typeof coinPackages[0] | null>(null);
  const [showTransactions, setShowTransactions] = useState(true);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [packageToConfirm, setPackageToConfirm] = useState<typeof coinPackages[0] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentStarted, setPaymentStarted] = useState(false);
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [message, setMessage] = useState<{ title: string; body: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [showPayPalForPackage, setShowPayPalForPackage] = useState<string | null>(null);

  // Función local de formateo por si no se proporciona la función desde props
  const formatNumberLocal = (num: number): string => {
    return formatNumber ? formatNumber(num) : num.toString();
  };

  // Usar el hook de moneda dinámica
  const { currency, packages: dynamicPackages, isLoading: loadingCurrency } = useCurrencyPackages();

  // Estado local para permitir cambio manual de moneda
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(currency);

  // Sincronizar selectedCurrency cuando currency del hook cambie
  useEffect(() => {
    setSelectedCurrency(currency);
  }, [currency]);

  // Usar los paquetes dinámicos en lugar de hardcoded
  const coinPackages = dynamicPackages;

  // Funciones auxiliares para obtener información de los paquetes
  const getPackageAmount = (packageId: string): number => {
    const pkg = coinPackages.find(p => p.id === packageId);
    return pkg ? pkg.price : 10; // Valor por defecto si no se encuentra
  };

  const getPackageCoins = (packageId: string): number => {
    const pkg = coinPackages.find(p => p.id === packageId);
    return pkg ? pkg.coins : 100; // Valor por defecto si no se encuentra
  };

  useEffect(() => {
    if (user) {
      loadUserCoins();
    }

    // Escuchar eventos de actualización de monedas
    const handleCoinsUpdated = () => {
      // Usar sistema de bloqueo para evitar procesamiento simultáneo
      const updateLockKey = 'COINS_UPDATE_IN_PROGRESS';

      if (typeof window !== 'undefined') {
        // Si ya hay una actualización en curso, salir
        if (window.sessionStorage.getItem(updateLockKey)) {
          console.log('CoinsManager: Actualización ya en curso, evitando procesamiento paralelo');
          return;
        }

        // Establecer bloqueo
        window.sessionStorage.setItem(updateLockKey, Date.now().toString());

        try {
          // Cargar datos con throttling
          console.log('CoinsManager: Cargando datos actualizados');
          loadUserCoins()
            .finally(() => {
              // Eliminar bloqueo después de completar
              setTimeout(() => {
                window.sessionStorage.removeItem(updateLockKey);
              }, 1000); // Mantener bloqueo 1 segundo después de completar
            });

          // Ejecutar callback si existe
          if (onCoinsUpdated) {
            onCoinsUpdated();
          }
        } catch (error) {
          console.error('Error al manejar actualización de monedas:', error);
          // Asegurar que el bloqueo se elimine en caso de error
          window.sessionStorage.removeItem(updateLockKey);
        }
      }
    };

    // Implementar un sistema de debounce para eventos rápidos
    let updateTimeout: number | null = null;

    // Función que implementa debounce para las actualizaciones
    const debouncedUpdate = () => {
      // Cancelar cualquier timeout pendiente
      if (updateTimeout !== null) {
        window.clearTimeout(updateTimeout);
      }

      // Establecer un nuevo timeout
      updateTimeout = window.setTimeout(() => {
        handleCoinsUpdated();
        updateTimeout = null;
      }, 300); // Esperar 300ms antes de procesar el evento
    };

    // Añadir event listener con la función de debounce
    window.addEventListener('coins-updated', debouncedUpdate);

    // Para compatibilidad con código antiguo
    window.addEventListener('coinsUpdated', debouncedUpdate);

    // Almacenar la URL base de la aplicación para los scripts de redirección
    if (typeof window !== 'undefined') {
      localStorage.setItem('app_base_url', window.location.origin);
    }

    // Cleanup
    return () => {
      window.removeEventListener('coins-updated', debouncedUpdate);
      window.removeEventListener('coinsUpdated', debouncedUpdate);

      // Limpiar cualquier timeout pendiente
      if (updateTimeout !== null) {
        window.clearTimeout(updateTimeout);
      }

      // Limpiar cualquier bloqueo pendiente al desmontar
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem('COINS_UPDATE_IN_PROGRESS');
      }
    };
  }, [user]);

  const loadUserCoins = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const coins = await getUserCoins(user.id);
      setUserCoins(coins);

      const txs = await getUserTransactions(user.id);
      setTransactions(txs);
    } catch (error) {
      console.error('Error al cargar datos de monedas:', error);
    } finally {
      setLoading(false);
    }
  };

  const confirmBuyCoins = async (packageId: string) => {
    const pkg = coinPackages.find(p => p.id === packageId);
    if (!pkg) {
      toast.error('Paquete no encontrado');
      return;
    }

    // Establecer el paquete seleccionado y proceder directamente con la compra
    setSelectedPackage(pkg);

    // Proceder directamente con la compra sin necesidad de seleccionar previamente
    if (!user) {
      toast.error('Debes iniciar sesión para comprar monedas');
      return;
    }

    // Verificar si ya hay una transacción en curso para evitar duplicados
    if (typeof window !== 'undefined') {
      try {
        // Verificar transacción pendiente en localStorage
        const pendingTxData = localStorage.getItem('pendingTransaction');
        if (pendingTxData) {
          const pendingTx = JSON.parse(pendingTxData);
          const now = Date.now();
          const txTime = pendingTx.timestamp || 0;

          // Si la transacción pendiente es reciente (menos de 5 minutos) y es para el mismo paquete
          if (now - txTime < 5 * 60 * 1000 && pendingTx.package_id === pkg.id) {
            const continueAnyway = window.confirm(
              `Ya existe una transacción pendiente para este paquete iniciada hace poco. ¿Deseas iniciar una nueva de todos modos?`
            );

            if (!continueAnyway) {
              toast.success('Operación cancelada. Puedes verificar el estado de tu pago anterior.');
              return;
            }
          }
        }
      } catch (e) {
        console.error('Error al verificar transacciones pendientes:', e);
      }
    }

    setIsLoading(true);

    try {
      // Obtener la URL base de la aplicación (para construcción de URLs)
      const baseUrl = window.location.origin;
      console.log('URL base:', baseUrl);

      // Guardar el ID del usuario actual en localStorage
      const userId = user?.id || '';
      localStorage.setItem('current_user_id', userId);

      // Crear transacción en el servidor
      const paykuService = PaykuService.getInstance();
      const response = await paykuService.createTransaction(
        userId,
        packageId,
        pkg.price,
        pkg.coins
      );

      console.log('Respuesta de creación de transacción:', response);

      if (response && response.success && response.payment_url) {
        // Guardar información de la transacción en sessionStorage para recuperarla después
        const transactionData = {
          id: response.transaction_id || '',
          package_id: pkg.id,
          amount: pkg.price,
          coins: pkg.coins,
          timestamp: Date.now(),
          user_id: user.id,
          return_url: `${window.location.origin}/verify-payment?txid=${response.transaction_id || ''}&status=success`
        };

        sessionStorage.setItem('pendingTransaction', JSON.stringify(transactionData));
        localStorage.setItem('pendingTransaction', JSON.stringify(transactionData));

        // Guardar el último ID de transacción para verificación posterior
        localStorage.setItem('last_payku_transaction', response.transaction_id || '');

        // Guardar la URL de retorno específica para Payku
        localStorage.setItem('payku_return_url', `${window.location.origin}/verify-payment?txid=${response.transaction_id || ''}&status=success`);

        // Guardar datos de transacción para el script de redirección
        localStorage.setItem('payku_transaction', JSON.stringify({
          transaction_id: response.transaction_id || '',
          return_url: `${window.location.origin}/verify-payment?txid=${response.transaction_id || ''}&status=success`,
          amount: pkg.price,
          coins: pkg.coins,
          package_id: pkg.id,
          created_at: new Date().toISOString()
        }));

        // Guardar la URL de pago en localStorage para la redirección
        localStorage.setItem('payku_payment_url', response.payment_url);

        // Limpiar cualquier bloqueo de verificación existente antes de iniciar nueva compra
        if (typeof window !== 'undefined' && window.sessionStorage) {
          const keys = Object.keys(window.sessionStorage);
          const processingKeys = keys.filter(k => k.startsWith('processing_tx_'));

          if (processingKeys.length > 0) {
            console.log('Limpiando bloqueos de verificación previos antes de nueva compra:', processingKeys);
            processingKeys.forEach(key => window.sessionStorage.removeItem(key));
          }

          // Eliminar verificación global activa
          window.sessionStorage.removeItem('global_verification_active');
        }

        // Mostrar toast avisando que se redirigirá al usuario
        toast.success(
          'Redirigiendo a la página de pago...',
          { duration: 2000 }
        );

        // Pequeña pausa para que el usuario vea el mensaje
        setTimeout(() => {
          // Redirigir directamente a la URL de Payku sin página intermedia
          window.location.href = response.payment_url || '';
        }, 1000);

        // Al redirigir en la misma ventana, ya no necesitamos este event listener
        // ni mostrar las instrucciones
        setPaymentStarted(false);
      } else {
        toast.error(response?.message || 'No se pudo crear la transacción');
      }
    } catch (error) {
      console.error('Error al iniciar pago:', error);
      toast.error('Error al iniciar el pago: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartPurchase = (pkg: typeof coinPackages[0]) => {
    // Usar el flujo de Payku para CLP
    setPackageToConfirm(pkg);
    setShowConfirmation(true);
  };

  const handleCancelPurchase = () => {
    setShowConfirmation(false);
    setPackageToConfirm(null);
  };

  const handleConfirmPurchase = () => {
    if (packageToConfirm) {
      confirmBuyCoins(packageToConfirm.id);
      setShowConfirmation(false);
      setPackageToConfirm(null);
    }
  };

  const handleBuyCoins = async () => {
    if (!user) {
      toast.error('Debes iniciar sesión para comprar monedas');
      return;
    }

    if (!selectedPackage) {
      toast.error('Por favor, selecciona un paquete de monedas');
      return;
    }

    handleStartPurchase(selectedPackage);
  };

  const handleRedeemCoupon = async () => {
    if (!user) {
      toast.error('Debes iniciar sesión para usar cupones');
      return;
    }

    if (!couponCode.trim()) {
      toast.error('Por favor, ingresa un código de cupón válido');
      return;
    }

    setValidatingCoupon(true);
    try {
      const result = await useCoupon(user.id, couponCode.trim());

      if (result.success) {
        toast.success(result.message);
        setCouponCode('');
        setShowCouponInput(false);
        // Recargar saldo de monedas
        loadUserCoins();
        if (onCoinsUpdated) {
          onCoinsUpdated();
        }
        // Disparar evento para que otros componentes sepan que las monedas cambiaron
        window.dispatchEvent(new CustomEvent('coins-updated'));
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error al canjear cupón:', error);
      toast.error('Error al procesar el cupón. Inténtalo de nuevo más tarde.');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const renderConfirmationModal = () => {
    if (!showConfirmation || !packageToConfirm) {
      return null;
    }

    return (
      <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[60] animate-fadeIn">
        <div className="relative z-10 w-full max-w-md mx-auto bg-gradient-to-br from-gray-900 to-gray-800 p-6 rounded-xl border border-blue-500/30 shadow-2xl">
          {/* Efectos de fondo */}
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-500/10 rounded-full blur-xl"></div>
          <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-purple-500/10 rounded-full blur-xl"></div>

          {/* Encabezado de confirmación */}
          <div className="text-center mb-6">
            <div className="inline-block p-3 bg-blue-600/20 rounded-full mb-3">
              <Sparkles className="h-8 w-8 text-blue-400" />
            </div>
            <h3 className="text-xl font-bold text-white">Confirmar compra</h3>
            <p className="text-gray-300 text-sm mt-1">
              Estás a punto de realizar el siguiente pago
            </p>
          </div>

          {/* Detalles del paquete */}
          <div className="bg-gray-800/80 rounded-lg p-4 mb-5 border border-gray-700">
            <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-700">
              <span className="text-gray-300">Paquete</span>
              <span className="font-semibold text-white">{packageToConfirm.name}</span>
            </div>

            <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-700">
              <span className="text-gray-300">Monedas</span>
              <span className="font-semibold text-yellow-400">{packageToConfirm.coins} monedas</span>
            </div>

            <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-700">
              <span className="text-gray-300">CVs que podrás generar</span>
              <span className="font-semibold text-green-400">Hasta {packageToConfirm.cvs} CV{packageToConfirm.cvs > 1 ? 's' : ''}</span>
            </div>

            {packageToConfirm.discount > 0 && (
              <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-700">
                <span className="text-gray-300">Descuento</span>
                <span className="font-semibold text-green-400">{packageToConfirm.discount}% OFF</span>
              </div>
            )}

            <div className="flex justify-between items-center mt-2">
              <span className="text-gray-300 font-medium">Precio final</span>
              <span className="text-xl font-bold text-white">${packageToConfirm.price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</span>
            </div>
          </div>

          {/* Sección de cupón */}
          {!showCouponInput ? (
            <button
              onClick={() => setShowCouponInput(true)}
              className="w-full mb-5 py-2 px-3 bg-blue-900/20 rounded-lg text-blue-400 hover:text-blue-300 text-sm flex items-center justify-center"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Tengo un código de cupón
            </button>
          ) : (
            <div className="mb-5 bg-blue-900/20 rounded-lg p-3">
              <div className="flex mb-2">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder="Ingresa tu código"
                  className="flex-1 bg-gray-800 text-white border border-gray-700 rounded-l px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={validatingCoupon}
                />
                <button
                  onClick={handleRedeemCoupon}
                  disabled={validatingCoupon || !couponCode.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-r flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {validatingCoupon ? (
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  <span>Aplicar</span>
                </button>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowCouponInput(false)}
                  className="text-xs text-gray-400 hover:text-gray-300"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Información de pago */}
          <div className="bg-blue-900/20 rounded-lg p-3 mb-5 text-sm">
            <p className="text-blue-200 flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                Serás redirigido a WebPay para realizar el pago de forma segura. Una vez completado, las monedas se añadirán automáticamente a tu cuenta.
              </span>
            </p>
          </div>

          {/* Botones de acción */}
          <div className="flex gap-3">
            <button
              onClick={handleCancelPurchase}
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-gray-300 font-medium rounded-lg transition-colors"
            >
              Cancelar
            </button>

            <button
              onClick={handleConfirmPurchase}
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  <span>Procesando...</span>
                </>
              ) : (
                <>
                  <span className="w-4 h-4 text-green-400">✓</span>
                  <span>Confirmar pago</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calcular estadísticas de transacciones
  const stats = useMemo(() => {
    if (!transactions.length) {
      return {
        totalCoins: userCoins?.balance || 0,
        totalSpent: 0,
        totalPurchased: 0,
        purchaseCount: 0,
        cvsGenerated: 0
      };
    }

    let totalSpent = 0;
    let totalPurchased = 0;
    let purchaseCount = 0;
    let cvsGenerated = 0;

    transactions.forEach(tx => {
      if (tx.amount < 0) {
        totalSpent += Math.abs(tx.amount);
        // Contar generaciones de CV (cada generación cuesta 1 moneda)
        if (tx.description.includes('Generación de CV')) {
          cvsGenerated++;
        }
      } else {
        totalPurchased += tx.amount;
        purchaseCount++;
      }
    });

    return {
      totalCoins: userCoins?.balance || 0,
      totalSpent,
      totalPurchased,
      purchaseCount,
      cvsGenerated
    };
  }, [transactions, userCoins]);

  // Limitar transacciones para mostrar solo las últimas 5 por defecto
  const displayedTransactions = useMemo(() => {
    return showAllTransactions ? transactions : transactions.slice(0, 5);
  }, [transactions, showAllTransactions]);

  // Función para iniciar un pago con Payku
  const initiatePayment = async (packageId: string) => {
    try {
      setIsLoading(true);
      setPaymentStarted(false);

      // Obtener el paquete seleccionado
      const pkg = coinPackages.find(p => p.id === packageId);
      if (!pkg) {
        toast.error('Paquete no encontrado');
        setIsLoading(false);
        return;
      }

      // Obtener la URL base de la aplicación (para construcción de URLs)
      const baseUrl = window.location.origin;
      console.log('URL base:', baseUrl);

      // Guardar el ID del usuario actual en localStorage
      const userId = user?.id || '';
      localStorage.setItem('current_user_id', userId);

      // Crear transacción en el servidor
      const paykuService = PaykuService.getInstance();
      const response = await paykuService.createTransaction(
        userId,
        packageId,
        pkg.price,
        pkg.coins
      );

      console.log('Respuesta de creación de transacción:', response);

      if (response && response.success && response.payment_url) {
        // Guardar la URL de pago en localStorage para la redirección
        localStorage.setItem('payku_payment_url', response.payment_url);

        // Redirigir a través de la página puente HTML codificando la URL de Payku
        const encodedPaykuUrl = encodeURIComponent(response.payment_url);
        const redirectUrl = `${baseUrl}/payku-redirect.html?payku_url=${encodedPaykuUrl}`;

        // Abrir ventana de pago
        const paymentWindow = window.open(redirectUrl, '_blank', 'width=1200,height=800');

        // Si la ventana se abrió correctamente
        if (paymentWindow) {
          // Configurar listener para mensajes de la ventana de pago
          window.addEventListener('message', function (event) {
            // Verificar si el mensaje es de nuestra aplicación/script de inyección
            if (event.data && event.data.type === 'PAYMENT_COMPLETED') {
              console.log('Mensaje de pago completado recibido:', event.data);
              // Redirigir a verificación después de recibir confirmación
              window.location.href = `/verify-payment?txid=${event.data.transaction_id || response.transaction_id}&source=payku_message`;
            }
          });

          // Mostrar mensaje para el usuario
          setPaymentStarted(true);
          toast.success(
            'Se ha abierto una nueva ventana con la página de pago. Por favor, completa el proceso allí. Una vez finalizado, regresa a esta ventana.',
            { duration: 8000 }
          );
        } else {
          // Si no se pudo abrir la ventana
          toast.error('No se pudo abrir la ventana de pago. Por favor, desactiva el bloqueador de ventanas emergentes y vuelve a intentarlo.');
        }
      } else {
        toast.error(response?.message || 'No se pudo crear la transacción');
      }
    } catch (error) {
      console.error('Error al iniciar pago:', error);
      toast.error('Error al iniciar el pago: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Renderizar instrucciones si el pago se ha iniciado
  const renderPaymentInstructions = () => {
    if (!paymentStarted) return null;

    return (
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 my-4">
        <h3 className="text-lg font-semibold text-indigo-800">Instrucciones de Pago</h3>
        <ol className="mt-2 space-y-2 text-sm text-gray-700">
          <li className="flex items-start">
            <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">1</span>
            <span>Completa el pago en la página de Payku</span>
          </li>
          <li className="flex items-start">
            <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">2</span>
            <span>Una vez realizado el pago, serás redirigido automáticamente a la página de verificación</span>
          </li>
          <li className="flex items-start">
            <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">3</span>
            <span>Si no eres redirigido después de 5 segundos, haz clic en el botón "Verificar Pago" que aparecerá al finalizar</span>
          </li>
        </ol>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Modal de confirmación */}
      {renderConfirmationModal()}

      {/* Paquetes de monedas */}
      <div className="bg-gradient-to-br from-blue-900/40 via-indigo-900/40 to-purple-900/40 rounded-xl p-6 border border-purple-500/20 shadow-lg overflow-hidden relative">
        {/* Efectos de fondo */}
        <div className="absolute -top-20 -right-20 w-60 h-60 bg-purple-500/10 rounded-full blur-2xl"></div>
        <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-blue-500/10 rounded-full blur-xl"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgMjAgMTAgTSAxMCAwIEwgMTAgMjAiIHN0cm9rZT0iIzgwODBmZjEwIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-10"></div>

        <div className="relative z-10">
          {/* Header con título */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
            <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200">Paquetes de monedas</h2>
          </div>



          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {coinPackages.map((pkg) => {
              const isRecommended = pkg.id === 'standard';
              const isPremium = pkg.id === 'premium';
              const isBestValue = pkg.bestValue;

              return (
                <div
                  key={pkg.id}
                  className={`relative bg-gray-800/60 backdrop-blur-sm rounded-xl overflow-hidden transition-all hover:transform hover:scale-[1.02] border ${isRecommended
                    ? 'border-blue-500/50 shadow-lg shadow-blue-500/20'
                    : isPremium
                      ? 'border-purple-500/50 shadow-lg shadow-purple-500/20'
                      : 'border-gray-600/50'
                    }`}
                >
                  {/* Efecto de fondo individual */}
                  <div className={`absolute inset-0 opacity-20 ${isRecommended
                    ? 'bg-gradient-to-br from-blue-600/30 to-blue-800/20'
                    : isPremium
                      ? 'bg-gradient-to-br from-purple-600/30 to-purple-800/20'
                      : 'bg-gradient-to-br from-gray-600/20 to-gray-800/10'
                    }`}></div>

                  {/* Etiqueta de descuento */}
                  {pkg.discount > 0 && (
                    <div className={`absolute -right-8 top-5 transform rotate-45 py-1 px-8 text-xs font-bold ${isRecommended ? 'bg-blue-600 text-white' : isPremium ? 'bg-purple-600 text-white' : 'bg-yellow-600 text-white'
                      }`}>
                      {pkg.discount}% OFF
                    </div>
                  )}

                  {/* Etiquetas para el paquete Premium */}
                  {isPremium ? (
                    <div className="absolute left-0 top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold py-1 px-3 rounded-br flex items-center">
                      MEJOR OFERTA
                    </div>
                  ) : null}

                  {/* Etiquetas para paquete Standard */}
                  {isRecommended && (
                    <div className="absolute left-0 top-0 bg-blue-600 text-white text-xs font-bold py-1 px-3 rounded-br">
                      MÁS POPULAR
                    </div>
                  )}

                  <div className="p-5 flex flex-col items-center text-center relative z-10">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${isRecommended
                      ? 'bg-blue-900/50 border border-blue-500/50'
                      : isPremium
                        ? 'bg-purple-900/50 border border-purple-500/50'
                        : 'bg-yellow-900/50 border border-yellow-500/50'
                      }`}>
                      <Sparkles className={`w-6 h-6 ${isRecommended
                        ? 'text-blue-400'
                        : isPremium
                          ? 'text-purple-400'
                          : 'text-yellow-400'
                        }`} />
                    </div>

                    <h3 className={`text-lg font-bold mb-1 ${isRecommended
                      ? 'text-blue-300'
                      : isPremium
                        ? 'text-purple-300'
                        : 'text-yellow-300'
                      }`}>{pkg.name}</h3>

                    <div className="mt-2 mb-4">
                      <div className="text-4xl font-extrabold text-yellow-400">
                        {formatNumberLocal(pkg.coins)}
                      </div>
                      <div className="text-yellow-500/70 text-sm mt-1">monedas</div>
                    </div>

                    {/* Información de CVs generables */}
                    <div className="bg-gray-700/50 rounded-lg py-2 px-3 mb-4 flex items-center">
                      <FileText className="w-4 h-4 text-gray-300 mr-2" />
                      <span className="text-gray-300 text-sm">
                        Alcanza para <span className="font-bold text-white">{pkg.cvs} {pkg.cvs === 1 ? 'CV' : 'CVs'}</span>
                      </span>
                    </div>

                    {/* Precio con precio tachado si hay descuento */}
                    <div className="mb-4">
                      {pkg.discount > 0 ? (
                        <div className="flex flex-col items-center">
                          <span className="text-gray-400 line-through text-sm">
                            {formatCurrency(Math.round(pkg.price * (1 + pkg.discount / 100)), selectedCurrency)}
                          </span>
                          <span className="text-2xl font-bold text-white">
                            {formatCurrency(pkg.price, selectedCurrency)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-2xl font-bold text-white">
                          {formatCurrency(pkg.price, selectedCurrency)}
                        </span>
                      )}
                    </div>

                    {/* Mostrar PayPal si USD está seleccionado y este paquete fue clickeado */}
                    {showPayPalForPackage === pkg.id && selectedCurrency === 'USD' ? (
                      <div className="w-full space-y-2">
                        <PayPalCheckout
                          packageId={pkg.id}
                          amount={pkg.amount}
                          userId={user?.id || ''}
                          onSuccess={(vcoins) => {
                            toast.success(`¡${vcoins} VCoins agregadas a tu cuenta!`);
                            setShowPayPalForPackage(null);
                            loadUserCoins();
                            if (onCoinsUpdated) {
                              onCoinsUpdated();
                            }
                            window.dispatchEvent(new CustomEvent('coins-updated'));
                          }}
                          onError={(error) => {
                            toast.error(error);
                            setShowPayPalForPackage(null);
                          }}
                        />
                        <button
                          onClick={() => setShowPayPalForPackage(null)}
                          className="w-full text-gray-400 hover:text-white text-sm py-2 transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleStartPurchase(pkg)}
                        disabled={isLoading}
                        className={`w-full py-2 px-4 rounded-lg font-medium transition-all flex items-center justify-center ${isLoading
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : isRecommended
                            ? 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white'
                            : isPremium
                              ? 'bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white'
                              : 'bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-700 hover:to-yellow-600 text-white'
                          }`}
                      >
                        <Zap className="w-4 h-4 mr-2" />
                        Comprar ahora
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sección de cupones (renderizada entre paquetes e historial) */}
      {renderCouponSection && renderCouponSection()}

      {/* Historial de transacciones */}
      <div className="bg-gradient-to-br from-blue-900/40 via-indigo-900/40 to-purple-900/40 rounded-xl p-6 border border-purple-500/20 shadow-lg overflow-hidden relative">
        {/* Efectos de fondo */}
        <div className="absolute -top-20 -right-20 w-60 h-60 bg-purple-500/10 rounded-full blur-2xl"></div>
        <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-blue-500/10 rounded-full blur-xl"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgMjAgMTAgTSAxMCAwIEwgMTAgMjAiIHN0cm9rZT0iIzgwODBmZjEwIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-10"></div>

        <div className="relative z-10">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200">Historial de transacciones</h2>
            <button
              onClick={() => setShowTransactions(!showTransactions)}
              className="text-gray-400 hover:text-white"
            >
              {showTransactions ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>

          {showTransactions && (
            <>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
                </div>
              ) : transactions.length > 0 ? (
                <div>
                  <div className="grid grid-cols-3 gap-2 mb-2 px-4 py-3 border-b border-gray-700/50">
                    <div className="text-xs font-medium text-gray-400 uppercase">Fecha</div>
                    <div className="text-xs font-medium text-gray-400 uppercase text-center">Tipo</div>
                    <div className="text-xs font-medium text-gray-400 uppercase text-right">Cantidad</div>
                  </div>

                  <div className="space-y-1 max-h-[300px] overflow-y-auto pr-1">
                    {displayedTransactions.map((tx) => (
                      <div key={tx.id} className="grid grid-cols-3 gap-2 px-4 py-3 bg-gray-800/30 rounded-lg hover:bg-gray-700/30 transition-colors">
                        <div className="text-sm text-gray-300 truncate">
                          {formatDate(tx.created_at).split(' ')[0]}<br />
                          <span className="text-xs text-gray-400">{formatDate(tx.created_at).split(' ')[1]}</span>
                        </div>

                        <div className="flex items-center justify-center">
                          {tx.amount < 0 ? (
                            // Si el monto es negativo, siempre mostrar como Gasto
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-900/60 text-red-300 border border-red-800/60">
                              <TrendingDown className="w-3 h-3 mr-1" />
                              Gasto
                            </span>
                          ) : tx.type === 'purchase' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-900/60 text-green-300 border border-green-800/60">
                              <TrendingUp className="w-3 h-3 mr-1" />
                              Compra
                            </span>
                          ) : tx.type === 'credit' && tx.description.includes('cupón') ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-900/60 text-purple-300 border border-purple-800/60">
                              <Sparkles className="w-3 h-3 mr-1" />
                              Cupón
                            </span>
                          ) : tx.type === 'credit' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-900/60 text-blue-300 border border-blue-800/60">
                              <TrendingUp className="w-3 h-3 mr-1" />
                              Crédito
                            </span>
                          ) : tx.type === 'usage' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-900/60 text-blue-300 border border-blue-800/60">
                              <FileText className="w-3 h-3 mr-1" />
                              Uso
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-900/60 text-gray-300 border border-gray-800/60">
                              <TrendingDown className="w-3 h-3 mr-1" />
                              Otro
                            </span>
                          )}
                        </div>

                        <div className="text-right font-medium">
                          {tx.amount > 0 ? (
                            <span className="text-green-400 flex items-center justify-end">
                              <span className="text-yellow-500 mr-1">⚡</span>
                              +{tx.amount}
                            </span>
                          ) : (
                            <span className="text-red-400 flex items-center justify-end">
                              <span className="text-yellow-500 mr-1">⚡</span>
                              -{Math.abs(tx.amount)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {transactions.length > 5 && (
                    <div className="mt-4 text-center">
                      <button
                        onClick={() => setShowAllTransactions(!showAllTransactions)}
                        className="px-4 py-2 text-blue-400 hover:text-blue-300 text-sm flex items-center mx-auto bg-gray-800/30 rounded-lg hover:bg-gray-700/30 transition-colors"
                      >
                        {showAllTransactions ? (
                          <>
                            <ChevronUp className="w-4 h-4 mr-1" />
                            Mostrar menos
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4 mr-1" />
                            Ver todas ({transactions.length})
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <History className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">No hay transacciones recientes</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Instrucciones de pago */}
      {renderPaymentInstructions()}
    </div>
  );
};

export default CoinsManager;
